<template>
    <AppLayout>
        <!-- Hero Section with CTA -->
        <div
            v-if="featuredImages.length"
            class="relative"
        >
            <HomeSlider :slider-images="featuredImages" />

            <!-- Overlay CTA -->
            <div
                class="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-transparent to-green-800"
            >
                <div class="text-center space-y-6 p-6 md:p-12">
                    <h1 class="text-2xl md:text-5xl text-white font-extrabold leading-tight">
                        T&T's Largest B2B Platform for Wholesalers & Shop-owners
                    </h1>
                    <inertia-link
                        :href="route('register')"
                        as="button"
                        class="inline-flex items-center px-6 py-3 text-white bg-green-600 rounded-lg shadow-lg hover:bg-green-500 transition"
                    >
                        Register Now
                        <RightArrowIcon class="w-5 ml-2" />
                    </inertia-link>
                    <div
                        class="w-10 h-10 mx-auto text-white animate-bounce mt-6"
                        @click="scrollBelow"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            class="w-full h-full"
                        >
                            <path
                                d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <!-- Stats -->
            <div
                class="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 bg-white shadow-lg rounded-lg px-6 py-4 w-11/12 md:w-3/4"
            >
                <div class="flex flex-wrap justify-around">
                    <div class="flex flex-col items-center text-center w-1/3">
                        <img
                            src="/storage/icons/icon-wholesalers-64.png"
                            class="h-10 mb-2"
                        />
                        <p class="text-green-600 font-bold text-xl">30K+</p>
                        <p class="text-sm text-gray-700">Retailers</p>
                    </div>
                    <div class="flex flex-col items-center text-center w-1/3">
                        <img
                            src="/storage/icons/icon-credit-card-64.png"
                            class="h-10 mb-2"
                        />
                        <p class="text-green-600 font-bold text-xl">15K</p>
                        <p class="text-sm text-gray-700">Credits upto</p>
                    </div>
                    <div class="flex flex-col items-center text-center w-1/3">
                        <img
                            src="/storage/icons/icon-products-64.png"
                            class="h-10 mb-2"
                        />
                        <p class="text-green-600 font-bold text-xl">25K Sellers</p>
                        <p class="text-sm text-gray-700">Products from</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- KYC Section -->
        <div
            id="kyc"
            class="container mx-auto mt-32 px-4 md:px-12 lg:px-20"
        >
            <div class="bg-green-50 border border-green-200 p-6 md:p-12 text-center rounded-lg">
                <h2 class="text-2xl md:text-4xl font-bold text-green-700 mb-4">
                    Complete Shop KYC to Access Wholesale Prices
                </h2>
                <p class="text-gray-600 md:text-xl mb-2">Only verified shop owners can view wholesale prices.</p>
                <p class="text-gray-500 text-sm md:text-base mb-4">
                    Required documents: Company Registration Certificate, ID Proof, VAT Registration*
                </p>
                <inertia-link
                    :href="route('register')"
                    as="button"
                    class="inline-block px-8 py-3 text-white bg-green-600 rounded-full hover:bg-green-500 transition text-lg"
                >
                    Upload KYC
                </inertia-link>
            </div>
        </div>

        <!-- Categories and Brands -->
        <div
            v-for="(parentCategory, parentKey) in categories"
            :key="'parentCategory' + parentKey"
            class="container mx-auto mt-20 px-4 md:px-12 lg:px-20"
        >
            <!-- Parent Category Banner -->
            <div
                class="relative group rounded-xl overflow-hidden shadow-md cursor-pointer mb-10"
                @click="goto('products.index', { categoryIds: [parentCategory.id] })"
            >
                <img
                    :src="parentCategory.banner_path"
                    :alt="parentCategory.name"
                    class="object-cover w-full h-56 transition-transform duration-300 group-hover:scale-105"
                />
                <div class="absolute inset-0 bg-green-800 bg-opacity-40 flex items-center justify-center">
                    <div class="text-white text-center">
                        <h3 class="text-2xl md:text-4xl font-extrabold uppercase tracking-wide">
                            {{ parentCategory.name }}
                        </h3>
                        <p class="mt-2 inline-block px-4 py-1 text-sm bg-white text-green-800 rounded">+ Explore</p>
                    </div>
                </div>
            </div>

            <!-- Child Categories Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div
                    v-for="(childCategory, i) in firstSix(parentCategory.children)"
                    :key="i"
                    class="bg-green-50 rounded-lg overflow-hidden shadow hover:shadow-md transition cursor-pointer"
                    @click="goto('products.index', { categoryIds: [childCategory.id] })"
                >
                    <img
                        :src="childCategory.banner_path"
                        class="h-40 w-full object-cover"
                    />
                    <div class="p-4 text-center font-bold uppercase text-green-700">{{ childCategory.name }}</div>
                </div>
            </div>

            <!-- View More Link -->
            <div
                v-if="parentCategory.children.length > 6"
                class="text-right mt-4"
            >
                <span
                    class="text-green-500 font-semibold cursor-pointer"
                    @click="goto('categories.index')"
                    >View More</span
                >
            </div>

            <!-- Top Brands Carousel -->
            <div
                v-if="parentCategory.brands.length"
                class="mt-10"
            >
                <h4 class="text-center text-xl md:text-3xl font-bold text-green-700 mb-4">Top Brands</h4>
                <div class="flex flex-wrap justify-center gap-4">
                    <img
                        v-for="(brand, bIdx) in parentCategory.brands.slice(0, 9)"
                        :key="bIdx"
                        :src="
                            brand.images.length
                                ? brand.images[0].image_path
                                : `https://ui-avatars.com/api/?name=${brand.name}&color=7F9CF5&background=EBF4FF`
                        "
                        :alt="brand.name"
                        class="h-16 w-16 md:h-20 md:w-20 rounded-full ring-2 ring-green-300 p-1 hover:scale-110 transition cursor-pointer"
                        @click="goto('products.index', { brandIds: [brand.id] })"
                    />
                </div>
            </div>
        </div>

        <HowTo />
    </AppLayout>
</template>

<script>
    import AppLayout from '@/Layouts/AppLayout.vue';
    import RightArrowIcon from '@/Components/Icons/RightArrowIcon.vue';
    import HowTo from '@/Components/HowTo.vue';
    import HomeSlider from '@/Components/HomeSlider.vue';

    export default {
        name: 'Dashboard',
        components: {
            AppLayout,
            HomeSlider,
            RightArrowIcon,
            HowTo,
        },

        metaInfo() {
            return {
                title: 'B2B Shopping',
                titleTemplate: 'Ecom - %s',
            };
        },

        props: {
            categories: {
                type: [Object, Array],
                required: true,
            },

            featuredImages: {
                type: Array,
                required: true,
            },
        },

        data() {
            return {
                colors: ['green', 'indigo', 'blue', 'yellow', 'purple', 'red', 'pink'], // colors used by tailwind
            };
        },

        methods: {
            firstSix(array) {
                if (array.length > 6) return array.slice(0, 6);
                return array;
            },

            scrollBelow() {
                const element = document.getElementById('kyc');
                const headerOffset = 100;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition - headerOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            },
        },
    };
</script>

<style>
    .banner-height {
        height: calc(100vh - 65px);
    }

    @media only screen and (max-width: 600px) {
        .banner-height {
            height: 30vh;
        }
    }
</style>
